import { useMemo } from 'react'
import { EntityMediaModal, type EntityMediaModalMessages } from '@/shared/media'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AssetHolding } from '@/modules/inventory/holdings'
import {
  deleteBatchMedia,
  getBatchMedia,
  getBatchMediaUrl,
  setBatchMediaFavorite,
  updateBatchMediaName,
  uploadBatchMedia,
} from '@/modules/inventory/holdings/batches/batchMediaService'

interface BatchMediaModalProps {
  batch: AssetHolding
  onClose: () => void
}

const batchMediaService = {
  getMedia: getBatchMedia,
  uploadMedia: uploadBatchMedia,
  getMediaUrl: getBatchMediaUrl,
  updateMediaName: updateBatchMediaName,
  setFavorite: setBatchMediaFavorite,
  deleteMedia: deleteBatchMedia,
}

export function BatchMediaModal({ batch, onClose }: BatchMediaModalProps) {
  const { t } = useTranslation()

  const messages = useMemo<EntityMediaModalMessages>(() => {
    const tm = (key: string, params?: Record<string, string>) =>
      t(`registrations.batches.media.${key}`, params)

    return {
      title: tm('title'),
      attachedTitle: (count) => tm('attachedTitle', { count: String(count) }),
      empty: tm('empty'),
      loading: tm('loading'),
      newNameLabel: tm('newNameLabel'),
      newNamePlaceholder: tm('newNamePlaceholder'),
      uploading: tm('uploading'),
      dropzone: tm('dropzone'),
      hint: tm('hint'),
      types: {
        image: tm('types.image'),
        video: tm('types.video'),
      },
      actions: {
        close: tm('actions.close'),
        confirm: tm('actions.confirm'),
        open: tm('actions.open'),
        favorite: tm('actions.favorite'),
        editName: tm('actions.editName'),
        remove: tm('actions.remove'),
      },
      messages: {
        loadError: tm('messages.loadError'),
        uploadSuccess: tm('messages.uploadSuccess'),
        openError: tm('messages.openError'),
        deleteTitle: tm('messages.deleteTitle'),
        deleteConfirm: (name) => tm('messages.deleteConfirm', { name }),
        deleteSuccess: tm('messages.deleteSuccess'),
        renameSuccess: tm('messages.renameSuccess'),
        favoriteSuccess: tm('messages.favoriteSuccess'),
      },
    }
  }, [t])

  return (
    <EntityMediaModal
      entityId={batch.id}
      unitId={batch.unit_id}
      service={batchMediaService}
      messages={messages}
      onClose={onClose}
    />
  )
}
